import React, { CSSProperties } from 'react';

// Design System Colors
export const colors = {
  primary: '#0074D9',
  primaryDark: '#0056A3',
  primaryLight: '#E3F2FD',
  success: '#2ECC40',
  successDark: '#228B22',
  successLight: '#D4EDDA',
  warning: '#FF851B',
  warningDark: '#E67600',
  warningLight: '#FFF3CD',
  danger: '#FF4136',
  dangerDark: '#CC0000',
  dangerLight: '#F8D7DA',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray500: '#6B7280',
  gray700: '#374151',
  gray900: '#111827',
  white: '#FFFFFF',
  black: '#000000'
};

// Button Component
interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'success' | 'danger' | 'secondary' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  style?: CSSProperties;
  icon?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  type = 'button',
  style = {},
  icon
}) => {
  const baseStyle: CSSProperties = {
    border: 'none',
    borderRadius: '8px',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
    opacity: disabled ? 0.6 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    justifyContent: 'center'
  };

  const sizeStyles: Record<string, CSSProperties> = {
    sm: { padding: '8px 16px', fontSize: '13px' },
    md: { padding: '12px 24px', fontSize: '14px' },
    lg: { padding: '14px 32px', fontSize: '16px' }
  };

  const variantStyles: Record<string, CSSProperties> = {
    primary: { background: colors.primary, color: colors.white },
    success: { background: colors.success, color: colors.white },
    danger: { background: colors.danger, color: colors.white },
    warning: { background: colors.warning, color: colors.white },
    secondary: { 
      background: colors.gray100, 
      color: colors.gray700, 
      border: `1px solid ${colors.gray300}` 
    }
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{ ...baseStyle, ...sizeStyles[size], ...variantStyles[variant], ...style }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  );
};

// Input Component
interface InputProps {
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  style?: CSSProperties;
}

export const Input: React.FC<InputProps> = ({
  value,
  onChange,
  type = 'text',
  placeholder,
  label,
  required = false,
  disabled = false,
  error,
  style = {}
}) => {
  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    border: `1px solid ${error ? colors.danger : colors.gray300}`,
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
    ...style
  };

  const labelStyle: CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: '8px',
    color: colors.gray700
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      {label && (
        <label style={labelStyle}>
          {label} {required && <span style={{ color: colors.danger }}>*</span>}
        </label>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        style={inputStyle}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = colors.primary;
          e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.primaryLight}`;
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = error ? colors.danger : colors.gray300;
          e.currentTarget.style.boxShadow = 'none';
        }}
      />
      {error && <p style={{ color: colors.danger, fontSize: '13px', marginTop: '4px' }}>{error}</p>}
    </div>
  );
};

// Card Component
interface CardProps {
  children: React.ReactNode;
  title?: string;
  style?: CSSProperties;
}

export const Card: React.FC<CardProps> = ({ children, title, style = {} }) => {
  const cardStyle: CSSProperties = {
    background: colors.white,
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${colors.gray200}`,
    ...style
  };

  return (
    <div style={cardStyle}>
      {title && <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px', fontWeight: 600 }}>{title}</h2>}
      {children}
    </div>
  );
};

// Badge Component
interface BadgeProps {
  children: React.ReactNode;
  variant?: 'success' | 'warning' | 'danger' | 'primary';
  style?: CSSProperties;
}

export const Badge: React.FC<BadgeProps> = ({ children, variant = 'primary', style = {} }) => {
  const variantStyles: Record<string, CSSProperties> = {
    success: { background: colors.successLight, color: colors.successDark },
    warning: { background: colors.warningLight, color: colors.warningDark },
    danger: { background: colors.dangerLight, color: colors.dangerDark },
    primary: { background: colors.primaryLight, color: colors.primaryDark }
  };

  const badgeStyle: CSSProperties = {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    ...variantStyles[variant],
    ...style
  };

  return <span style={badgeStyle}>{children}</span>;
};

// ActionButton Component - Unified button for all CRUD actions
interface ActionButtonProps {
  variant: 'add' | 'view' | 'edit' | 'delete' | 'save' | 'cancel';
  onClick: () => void;
  label?: string;
  disabled?: boolean;
  style?: CSSProperties;
}

export const ActionButton: React.FC<ActionButtonProps> = ({
  variant,
  onClick,
  label,
  disabled = false,
  style = {}
}) => {
  const config = {
    add: { color: colors.success, icon: '➕', text: label || 'Add' },
    view: { color: colors.primary, icon: '👁️', text: label || 'View' },
    edit: { color: colors.warning, icon: '✏️', text: label || 'Edit' },
    delete: { color: colors.danger, icon: '🗑️', text: label || 'Delete' },
    save: { color: colors.success, icon: '💾', text: label || 'Save' },
    cancel: { color: colors.gray500, icon: '❌', text: label || 'Cancel' }
  };

  const { color, icon, text } = config[variant];

  const buttonStyle: CSSProperties = {
    padding: '8px 14px',
    backgroundColor: color,
    color: colors.white,
    border: 'none',
    borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    transition: 'all 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    opacity: disabled ? 0.6 : 1,
    fontFamily: 'inherit',
    ...style
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={buttonStyle}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <span>{icon}</span>
      <span>{text}</span>
    </button>
  );
};

// PageLayout Component - Consistent page structure
interface PageLayoutProps {
  children: React.ReactNode;
  title: string;
  action?: React.ReactNode;
}

export const PageLayout: React.FC<PageLayoutProps> = ({ children, title, action }) => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '30px auto',
      padding: isMobile ? '16px' : '30px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '25px',
        gap: '16px',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <h2 style={{
          margin: 0,
          color: colors.gray900,
          fontSize: isMobile ? '22px' : '28px',
          fontWeight: 700,
          minWidth: 0
        }}>
          {title}
        </h2>
        {action && <div style={{ flexShrink: 0, width: isMobile ? '100%' : 'auto' }}>{action}</div>}
      </div>
      {children}
    </div>
  );
};

// FormLayout Component - Consistent form structure
interface FormLayoutProps {
  children: React.ReactNode;
  title?: string;
  onSubmit?: (e: React.FormEvent) => void;
}

export const FormLayout: React.FC<FormLayoutProps> = ({ children, title, onSubmit }) => {
  return (
    <div style={{
      maxWidth: '800px',
      margin: '40px auto',
      padding: '40px',
      background: colors.white,
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      border: `1px solid ${colors.gray200}`
    }}>
      {title && (
        <h2 style={{
          marginTop: 0,
          marginBottom: '30px',
          fontSize: '24px',
          fontWeight: 700,
          color: colors.gray900
        }}>
          {title}
        </h2>
      )}
      <form onSubmit={onSubmit}>
        {children}
      </form>
    </div>
  );
};

// Image Upload Component
interface ImageUploadProps {
  label?: string;
  currentImage?: string;
  onUpload: (file: File) => void;
  onRemove?: () => void;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({ 
  label = 'Upload Image',
  currentImage,
  onUpload,
  onRemove
}) => {
  const [preview, setPreview] = React.useState<string | null>(currentImage || null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      // Call upload handler
      onUpload(file);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    if (onRemove) onRemove();
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: colors.gray700 }}>
        {label}
      </label>
      
      {preview ? (
        <div style={{ position: 'relative', width: 'fit-content' }}>
          <img 
            src={preview} 
            alt="Preview" 
            style={{ 
              width: '150px', 
              height: '150px', 
              objectFit: 'cover', 
              borderRadius: '8px',
              border: `2px solid ${colors.gray300}`
            }} 
          />
          <button
            onClick={handleRemove}
            style={{
              position: 'absolute',
              top: '-10px',
              right: '-10px',
              background: colors.danger,
              color: colors.white,
              border: 'none',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <label 
          style={{
            display: 'inline-block',
            padding: '12px 24px',
            background: colors.gray100,
            border: `2px dashed ${colors.gray300}`,
            borderRadius: '8px',
            cursor: 'pointer',
            textAlign: 'center',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = colors.primary;
            e.currentTarget.style.background = colors.primaryLight;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = colors.gray300;
            e.currentTarget.style.background = colors.gray100;
          }}
        >
          <span style={{ fontSize: '14px', color: colors.gray700 }}>📸 Click to upload</span>
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </label>
      )}
    </div>
  );
};
